import { combineReducers, configureStore } from '@reduxjs/toolkit'

import createSagaMiddleware from 'redux-saga'
import hardSet from 'redux-persist/es/stateReconciler/hardSet';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/es/storage';
import rootSaga from '@/sagas/sagas'
import { postsReducer } from '@/sagas/postsReducer'
import { searchReducer } from '@/sagas/searchReducer'
import { notificationsReducer } from '@/sagas/notificationsReducer'
import { settingsReducer } from '@/sagas/settingsReducer'
import { basketReducer } from '@/sagas/basketReducer';
import { dialogReducer } from '@/sagas/dialogReducer';

const sagaMiddleware = createSagaMiddleware()

// Configure persistence for search reducer with blacklist for parentDetails
const searchPersistConfig = {
    key: 'search',
    storage: storage,
    blacklist: ['parentDetails'], // Exclude parentDetails to prevent quota issues
    // Transform to limit data size
    transforms: [
        {
            in: (state: any) => {
                // Limit the number of items to prevent storage bloat
                if (state && state.items && state.items.length > 1000) {
                    return {
                        ...state,
                        items: state.items.slice(-500) // Keep only last 500 items
                    };
                }
                return state;
            },
            out: (state: any) => state
        }
    ]
};

const rootReducer: any = combineReducers({
    posts: postsReducer,
    search: persistReducer(searchPersistConfig, searchReducer),
    notifications: notificationsReducer,
    settings: settingsReducer,
    basket: basketReducer,
    dialog: dialogReducer
})

const persistConfig: any = {
    key: 'root',
    storage: storage,
    stateReconciler: hardSet
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleWare) => getDefaultMiddleWare({ serializableCheck: false }).concat(sagaMiddleware)
});
sagaMiddleware.run(rootSaga)

// Storage quota error handling
const handleStorageQuotaError = () => {
    console.warn('LocalStorage quota exceeded, clearing parentDetails and attempting to continue');
    try {
        // Clear just the parentDetails from any existing persisted state
        const persistedState = localStorage.getItem('persist:root');
        if (persistedState) {
            const parsedState = JSON.parse(persistedState);
            if (parsedState.search) {
                const searchState = JSON.parse(parsedState.search);
                if (searchState.parentDetails) {
                    searchState.parentDetails = {};
                    parsedState.search = JSON.stringify(searchState);
                    localStorage.setItem('persist:root', JSON.stringify(parsedState));
                }
            }
        }
    } catch (error) {
        console.error('Failed to clean localStorage:', error);
        // As last resort, clear all localStorage
        localStorage.clear();
    }
};

// Add global error handler for quota exceeded errors
if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
        if (event.error && event.error.name === 'QuotaExceededError') {
            handleStorageQuotaError();
        }
    });
}

export default store;
export const persistor = persistStore(store)